import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { Box, Typography, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import qs from 'qs';
import dayjs from 'dayjs';
import { getCenterUserAttendStatistics } from '../../api/user';
import Loader from '../loader';

/*
 * 입장내역관리 통계 (#94).
 *
 * 참고한 타 CRM 화면은 요일·시간대를 반원 도넛으로 그렸는데, 여기서 할 일은 '언제 붐비나'
 * 즉 크기 비교다. 각도로 크기를 읽게 하는 도넛은 그 비교가 가장 어려운 형태라 막대로 바꿨다.
 * 대신 그 화면에 없던 일자별 추이를 넣었다 — 이 화면에서 가장 자주 묻는 질문이라서다.
 *
 * 팔레트는 검증기(dataviz) 전 항목 통과값이다 — 최악 인접 ΔE 17.7(정상)·15.3(적록).
 * 대비가 3:1 미만인 색이 있어 모든 막대에 값 라벨을 함께 둔다(색만으로 읽게 하지 않는다).
 */
const SERIES = {
  entry: '#14A79C',
  member: '#3D82E8',
};

const STATUS_COLORS = {
  유효회원: '#14A79C',
  만료임박: '#E09A26',
  사용예정: '#3D82E8',
  중지회원: '#8A63D2',
  만료회원: '#D94F6E',
};
const STATUS_FALLBACK = '#8A94A0';

// 성별은 선택 입력이라 빈 값이 흔하다. 미입력은 회색으로 눕혀 두 성별과 경쟁시키지 않는다.
const GENDER_META = {
  MALE: { label: '남성', color: '#3D82E8' },
  FEMALE: { label: '여성', color: '#D94F6E' },
  UNKNOWN: { label: '미입력', color: '#B6BDC4' },
};

const DOW_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const Wrap = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginTop: '8px',
});

const KpiRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: '10px',
});

const KpiCard = styled(Box)({
  border: '1px solid #E8ECEF',
  borderRadius: '12px',
  padding: '14px 16px',
  background: '#FBFCFD',
});

const KpiValue = styled(Typography)({
  fontSize: '22px',
  fontWeight: 800,
  color: '#1F2937',
  lineHeight: 1.2,
  fontVariantNumeric: 'tabular-nums',
});

const KpiLabel = styled(Typography)({
  fontSize: '12.5px',
  color: '#6B7280',
  marginTop: '4px',
});

const Panel = styled(Box)({
  border: '1px solid #E8ECEF',
  borderRadius: '12px',
  padding: '16px 18px 14px',
  background: '#fff',
});

const PanelTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 700,
  color: '#1F2937',
});

const PanelHint = styled(Typography)({
  fontSize: '12px',
  color: '#9CA3AF',
  marginTop: '2px',
  marginBottom: '12px',
});

const PanelRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: '16px',
});

// 세로 막대. 값 라벨을 항상 달아 색 대비가 낮아도 수치는 읽히게 한다.
const ColumnChart = ({ items, color, unit, emptyText }) => {
  const max = Math.max(1, ...items.map(item => item.count));
  const hasAny = items.some(item => item.count > 0);
  if (!hasAny) {
    return <Empty>{emptyText}</Empty>;
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '150px' }}>
      {items.map(item => (
        <Tooltip key={item.label} arrow placement="top" title={`${item.label} · ${item.count}${unit}`}>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'flex-end', height: '100%', gap: '4px' }}>
            <span style={{ fontSize: '11px', color: item.count ? '#4B5563' : '#C4CBD2',
              fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {item.count || ''}
            </span>
            <span style={{ width: '100%', maxWidth: '34px',
              height: `${Math.max(item.count ? 3 : 0, (item.count / max) * 100)}%`,
              background: item.count ? color : 'transparent',
              borderRadius: '4px 4px 0 0' }} />
            <span style={{ fontSize: '11px', color: '#8A94A0', whiteSpace: 'nowrap' }}>
              {item.label}
            </span>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
};

// 가로 막대. 항목 이름이 길고 개수가 적을 때(회원상태) 세로보다 읽기 쉽다.
const BarList = ({ items, unit, emptyText }) => {
  const max = Math.max(1, ...items.map(item => item.count));
  if (!items.length) {
    return <Empty>{emptyText}</Empty>;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {items.map(item => (
        <Box key={item.label} sx={{ display: 'grid', gridTemplateColumns: '84px 1fr 56px',
          alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '12.5px', color: '#4B5563', fontWeight: 600 }}>{item.label}</span>
          <span style={{ display: 'flex', height: '10px', borderRadius: '5px', background: '#EFF2F4' }}>
            <span style={{ width: `${(item.count / max) * 100}%`, background: item.color,
              borderRadius: '5px' }} />
          </span>
          <span style={{ fontSize: '12.5px', color: '#374151', fontWeight: 700, textAlign: 'right',
            fontVariantNumeric: 'tabular-nums' }}>
            {`${item.count}${unit}`}
          </span>
        </Box>
      ))}
    </Box>
  );
};

const Empty = styled(Box)({
  padding: '28px 0',
  textAlign: 'center',
  fontSize: '13px',
  color: '#9CA3AF',
});

const AttendHistoryStats = ({ apiQuery, enabled }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const token = user?.token;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['attendHistoryStats', apiQuery],
    // 통계는 페이지와 무관하다. 목록의 page/limit 은 떼고 보낸다.
    queryFn: () => {
      const { page, limit, ...rest } = apiQuery;
      return getCenterUserAttendStatistics(qs.stringify(rest), token);
    },
    enabled: !!token && enabled,
  });

  const dow = useMemo(
    () => (data?.byDayOfWeek || []).map((item, index) => ({
      label: DOW_LABELS[index], count: item.count,
    })),
    [data],
  );

  const hours = useMemo(
    () => (data?.byHour || []).map((item, index) => ({
      label: index % 3 === 0 ? String(index) : '', count: item.count,
    })),
    [data],
  );

  const daily = useMemo(
    () => (data?.daily || []).map(item => ({
      label: dayjs(item.date).format('M/D'), count: item.entryCount,
    })),
    [data],
  );

  const genders = useMemo(
    () => (data?.byGender || []).map(item => ({
      label: t(GENDER_META[item.key]?.label || item.key),
      count: item.count,
      color: GENDER_META[item.key]?.color || STATUS_FALLBACK,
    })),
    [data, t],
  );

  const statuses = useMemo(
    () => (data?.byMemberStatus || [])
      .map(item => ({
        label: item.key,
        count: item.count,
        color: STATUS_COLORS[item.key] || STATUS_FALLBACK,
      }))
      .sort((a, b) => b.count - a.count),
    [data],
  );

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}><Loader /></Box>;
  }
  if (isError || !data) {
    return <Empty>{t('통계를 불러오지 못했습니다')}</Empty>;
  }

  const kpis = [
    [data.entryCount, '입장 건수', '건'],
    [data.visitDayCount, '방문 일수', '일'],
    [data.memberCount, '방문 회원', '명'],
    [data.avgVisitDaysPerMember, '1인 평균 방문일', '일'],
    [data.revisitMemberCount, '재방문 회원', '명'],
    [data.avgEntriesPerActiveDay, '하루 평균 입장', '건'],
  ];

  return (
    <Wrap>
      <KpiRow>
        {kpis.map(([value, label, unit]) => (
          <KpiCard key={label}>
            <KpiValue>{`${value ?? 0}${t(unit)}`}</KpiValue>
            <KpiLabel>{t(label)}</KpiLabel>
          </KpiCard>
        ))}
      </KpiRow>

      <Panel>
        <PanelTitle>{t('일자별 입장')}</PanelTitle>
        {/* 참고 화면에 없던 축이다. '언제 붐볐나'는 기간을 좁혀 보지 않으면 안 보인다. */}
        <PanelHint>{t('기록이 있는 날만 표시됩니다')}</PanelHint>
        <ColumnChart items={daily} color={SERIES.entry} unit={t('건')}
          emptyText={t('기간 내 입장 기록이 없습니다')} />
      </Panel>

      <PanelRow>
        <Panel>
          <PanelTitle>{t('요일별 방문')}</PanelTitle>
          {/* 요일은 '몇 번 들어왔나'가 아니라 '며칠 왔나'로 세야 주기가 보인다. */}
          <PanelHint>{t('하루에 여러 번 들어와도 1일로 셉니다')}</PanelHint>
          <ColumnChart items={dow} color={SERIES.entry} unit={t('일')}
            emptyText={t('기간 내 방문이 없습니다')} />
        </Panel>

        <Panel>
          <PanelTitle>{t('시간대별 입장')}</PanelTitle>
          <PanelHint>{t('입장 시각 기준 · 0~23시')}</PanelHint>
          <ColumnChart items={hours} color={SERIES.member} unit={t('건')}
            emptyText={t('입장 시각이 남은 기록이 없습니다')} />
        </Panel>
      </PanelRow>

      <PanelRow>
        <Panel>
          <PanelTitle>{t('회원상태별 방문 회원')}</PanelTitle>
          {/* 상태는 회원의 성질이라 건수로 세면 자주 오는 사람이 부풀려진다. 회원 수로 센다. */}
          <PanelHint>{t('입장 건수가 아니라 회원 수입니다')}</PanelHint>
          <BarList items={statuses} unit={t('명')} emptyText={t('회원상태를 확인할 수 없습니다')} />
        </Panel>

        <Panel>
          <PanelTitle>{t('성별 방문 회원')}</PanelTitle>
          {/* 빼고 그리면 합이 방문 회원 수와 안 맞아 '왜 모자라냐'가 된다. 미입력을 드러낸다. */}
          <PanelHint>{t('성별은 선택 입력이라 미입력이 함께 표시됩니다')}</PanelHint>
          <BarList items={genders} unit={t('명')} emptyText={t('방문 회원이 없습니다')} />
        </Panel>
      </PanelRow>
    </Wrap>
  );
};

export default AttendHistoryStats;
